import React, { useState, useEffect, useRef, useMemo } from 'react';
import DateTimeSelector from './DateTimeSelector';
import user from '../assets/images/default.png';
import EditIcon from '@mui/icons-material/Edit';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import useApi from "../hooks/useApi";
import ProductService from '../services/product.service';
import CategoryService from '../services/category.service';
import DomainService from '../services/domain.service';
import BookingService from '../services/booking.service';
import useAuth from '../hooks/useAuth';
import Modal from "react-modal";
import { useNavigate, useLocation, useParams } from "react-router-dom";
import TherapistService from '../services/therapist.service';
import { Button } from '@mui/material';
import { useNotifications } from '../context/NotificationProvider';
import { useTranslation } from 'react-i18next';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import guestPlaceholder from '../assets/images/Frame_08.svg';
import { useLoading } from '../context/LoadingProvider';
import PromotionService from '../services/promotion.service';
import MenuHeader from "./MenuHeader";
import "../App.css";
import AvailableSlotsService from '../services/available_slots.service';
import { useTranslatableData } from '../hooks/useTranslatable';
import {
    addAdminZoneOverride,
    classifyAdminZoneEligibility,
    classifySelectedZoneCoverage,
    getFlyproId,
    getFlyproLabel,
} from '../shared/utils/adminZoneOverride';

function GuestCard({
    index,
    product,
    durations = [],
    collapsed,
    onToggle,
    onSelectDuration,
}) {
    const { t } = useTranslation(['commande_edit']);
    return (
        <div className="guest-card">
            <div
                className="guest-card__header"
                onClick={() => onToggle(index)}
                style={{ cursor: 'pointer' }}
            >
                <div className="guest-card__meta">
                    <img
                        src={guestPlaceholder}
                        alt=""
                        className="guest-card__avatar"
                    />
                    <span className="guest-card__label">{t('commande_edit:guest')} {index + 1}</span>                
                </div>
                <KeyboardArrowDownIcon
                    className={`guest-card__chevron ${collapsed ? '' : 'open'}`}
                />
            </div>

            {!collapsed && durations.length > 0 && (
                <div className="duration-tabs">
                    {durations.map((p) => (
                        <button
                            key={p.id}
                            type="button"
                            className={`duration-tab ${product?.id === p.id ? 'duration-tab--active' : ''
                                }`}
                            onClick={() => onSelectDuration(index, p)}
                        >
                            <span className="duration-tab__minutes">{p.duration}&nbsp;{t('commande_edit:min')}</span>
                            <span className="duration-tab__price">{p.price}&nbsp;{t('commande_edit:chf')}</span>
                        </button>
                    ))}
                </div>
            )}
        </div>
    );
}

/* --- DuoInfoModal ---- */
function DuoInfoModal({ open, onClose, t1, t2, onAskRemove }) {
    const { t } = useTranslation(['commande_edit']);

    const therapists = [t1, t2].filter(th => th && th.name);

    return (
        <Modal
            isOpen={open}
            onRequestClose={onClose}
            style={{
                content: {
                    width: '90%',
                    maxWidth: '480px',
                    maxHeight: '480px',
                    margin: 'auto',
                    padding: '30px',
                    borderRadius: '12px',
                    textAlign: 'center',
                },
            }}
        >
            <h2 style={{ marginBottom: 24 }}>{t('commande_edit:duo_massage')} {t('commande_edit:details')}</h2>
            {therapists.map((th, i) => (
                <div
                    key={i}
                    style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        background: '#FFF',
                        borderRadius: '8px',
                        padding: '12px 16px',
                        boxShadow: '0 2px 6px rgba(0,0,0,0.05)',
                        marginBottom: i < therapists.length - 1 ? 12 : 0,
                    }}
                >
                    <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                        <img
                            src={user}
                            alt=""
                            style={{
                                width: 40,
                                height: 40,
                                borderRadius: '50%',
                                objectFit: 'cover',
                                background: '#F0F0F0',
                            }}
                        />
                        <div style={{ textAlign: 'left' }}>
                            <div style={{ fontWeight: 500, fontSize: '16px', color: '#333' }}>
                                {th.name}
                            </div>
                            {th.products.map((p) => (
                                <div
                                    key={p.id}
                                    style={{ fontSize: '14px', color: '#555', marginTop: 4 }}
                                >
                                {`${Number(p.duration)} ${t('commande_edit:min')} – ${p.price} ${t('commande_edit:chf')}`}                                </div>
                            ))}
                        </div>
                    </div>

                    <DeleteOutlineIcon
                        sx={{ color: '#B00020', cursor: 'pointer', fontSize: 24 }}
                        onClick={() => onAskRemove(i)}
                    />
                </div>
            ))}

            <button
                className="btn btn-secondary"
                onClick={onClose}
            >
                {t('commande_edit:close')}
            </button>
        </Modal>
    );
}

function formatTimeHi(timeStr) {
    if (!timeStr) return "8:00";
    const [h, m] = timeStr.split(':');
    return `${Number(h)}:${String(m).padStart(2, '0')}`;
}

function getApiDateTime(selectedDate, selectedTime) {
    const pad = n => (n !== undefined && n !== null ? String(n).padStart(2, '0') : '00');
    const yyyy = selectedDate.getFullYear();
    const mm = pad(selectedDate.getMonth() + 1);
    const dd = pad(selectedDate.getDate());
    let [hour, minute] = selectedTime.split(':');
    hour = pad(hour);
    minute = pad(minute);
    return {
        dateStr: `${yyyy}-${mm}-${dd}`,
        timeStr: `${hour}:${minute}`,
        full: `${yyyy}-${mm}-${dd} ${hour}:${minute}`
    };
}

const hydrateProduct = async (prod, axiosPrivate) => {
    let category = prod.category;
    if (!category && prod.category_id) {
        const res = await CategoryService.index({ id: prod.category_id }, axiosPrivate);
        category = Array.isArray(res) ? res[0] : res;
    }
    let domain = prod.domain;
    let domainId = (category && category.domain_id) || prod.domain_id;
    if (!domain && domainId) {
        const res = await DomainService.index({ id: domainId }, axiosPrivate);
        domain = Array.isArray(res) ? res[0] : res;
    }
    return { ...prod, category, domain };
};

const fetchCategoryAndDomain = async (product, axiosPrivate) => {
    let category = product.category;
    if (!category && product.category_id) {
        category = await CategoryService.index(product.category_id, axiosPrivate);
    }
    let domain = product.domain;
    if (!domain && product.domain_id) {
        domain = await DomainService.index(product.domain_id, axiosPrivate);
    }
    return { category, domain };
};

function normalizeTherapistResponse(response) {
    if (Array.isArray(response)) {
        return response;
    }

    return Object.values(response || {})
        .flat()
        .filter(Boolean);
}

function BookingEditAdmin({ booking: bookingProp, bookingToEdit: bookingToEdit, onBack, onUpdate }) {

    const { bookingId } = useParams();
    const [booking, setBooking] = useState(null);
    const [loading, setLoading] = useState(false);
    const axiosPrivate = useApi();
    const handleBack = onBack || (() => navigate(-1));

    useEffect(() => {
        if (!booking && bookingId) {
            BookingService.index({
                id: bookingId,
                with: "products,therapists,therapists.media,therapists.zones,address,client,hotel,products.category"
            }, axiosPrivate)
                .then(res => setBooking(Array.isArray(res) ? res[0] : res));

            console.log('booking', booking);
        }
    }, [bookingId, axiosPrivate]);


    useEffect(() => {
        setMessage(booking?.message || '');
    }, [booking?.message]);

    const { t, i18n } = useTranslation(['commande_edit','service']);
    const loc = useLocation();
    const { auth } = useAuth();
    const notify = useNotifications();
    const loader = useLoading();
    const bookingDateInZurich = new Date(
        new Date(booking?.start_before).toLocaleString("en-US", { timeZone: "Europe/Zurich" })
    );
    const [selectedDate, setSelectedDate] = useState(new Date());
    const [availableHours, setAvailableHours] = useState({});

    useEffect(() => {
        if (booking?.start_before) {
            const dateInZurich = new Date(
                new Date(booking.start_before).toLocaleString("en-US", { timeZone: "Europe/Zurich" })
            );
            setSelectedDate(dateInZurich);
            setDaysToShow(getInitialDaysToShow(dateInZurich));
        }
    }, [booking?.start_before]);

    const [selectedTime, setSelectedTime] = useState('08:00');

    useEffect(() => {
        if (booking?.start_before) {
            const startDate = new Date(booking.start_before.replace(' ', 'T'));
            const timeStr = startDate
                .toLocaleTimeString('en-US', {
                    timeZone: "Europe/Zurich",
                    hour: '2-digit',
                    minute: '2-digit',
                    hour12: false
                });
            setSelectedTime(formatTimeHi(timeStr));
            console.log({ selectedTime })
        }
    }, [booking?.start_before]);

    const [selectedTherapists, setSelectedTherapists] = useState([]);

    useEffect(() => {
        if (booking) {
            const therapists = booking.therapists ?? (booking.therapist ? [booking.therapist] : []);
            setSelectedTherapists(booking.isDuo ? therapists.slice(0, 2) : [therapists[0]]);
        }
    }, [booking]);

    const [duo, setDuo] = useState(booking?.isDuo);
    const [message, setMessage] = useState(booking?.message || '');
    const [price, setPrice] = useState(booking?.isDuo ? (booking?.products ?? []).reduce((sum, p) => sum + (p?.price || 0), 0) : booking?.product?.price ?? 0);

    const [product] = useState(booking?.product);
    const [selectedProducts, setSelectedProducts] = useState([]); // Empty initially
    const translatedProducts = useTranslatableData(selectedProducts);

    useEffect(() => {
        if (!booking) {
            setSelectedProducts([]);
            return;
        }
        if (Array.isArray(booking.products) && booking.products.length > 0) {
            if (booking.isDuo) {
                const padded = [...booking.products, ...Array(2 - booking.products.length).fill(null)].slice(0, 2);
                setSelectedProducts(padded);
            } else {
                setSelectedProducts([booking.products[0]]);
            }
            console.log('selectedProducts', selectedProducts);
        } else if (booking.product) {
            setSelectedProducts([booking.product]);
        } else {
            setSelectedProducts([]);
        }
    }, [booking]);

    const originFlow = booking?.origin_flow;
    const [flowType, setFlowType] = useState(originFlow);

    const [categoryProductsList, setCategoryProductsList] = useState([[], []]);
    const [collapsedGuests, setCollapsedGuests] = useState(
        (Array.isArray(booking?.products) ? booking?.products : [booking?.product]).map(
            (_p, idx) => idx !== 0
        )
    );
    const [productDropdownOpen, setProductDropdownOpen] = useState(false);

    const toggleGuest = (i) =>
        setCollapsedGuests((prev) => {
            const next = [...prev];
            next[i] = !next[i];
            return next;
        });
    const firstProd = translatedProducts.find(Boolean);
    console.log({ translatedProducts })

    const [showBookingConfirmationModal, setShowBookingConfirmationModal] = useState(false);
    const [therapistIsAvailable, setTherapistIsAvailable] = useState(null);
    const [pendingBookingPayload, setPendingBookingPayload] = useState(null);
    // const formattedDate = selectedDate.toLocaleDateString('en-US', {
    //     weekday: 'long',
    //     day: '2-digit',
    //     month: 'long',
    //     year: 'numeric',
    // });

    const [duration, setDuration] = useState(0);

    useEffect(() => {
        const total = selectedProducts.filter(Boolean).reduce((sum, p) => sum + parseInt(p?.duration || 0, 10), 0);
        setDuration(total);
    }, [selectedProducts]);

    // const formattedTime = selectedTime;

    function formatSelectedTime(selectedTime, lang = 'fr') {
        if (!selectedTime) return '';
      
        const [h, m] = selectedTime.split(':').map(Number);
      
        if (lang === 'fr') {
          return `${h}h${m.toString().padStart(2, '0')}`;
        } else {
          const hour12 = h % 12 || 12;
          const ampm = h < 12 ? 'AM' : 'PM';
          return `${hour12}:${m.toString().padStart(2, '0')} ${ampm}`;
        }
      }
      

    const formattedDate = new Intl.DateTimeFormat(i18n.language, {
        day: 'numeric',
        month: 'long',
        year: 'numeric',
      }).format(selectedDate);
      
    const formattedTime = formatSelectedTime(selectedTime, i18n.language);


    const [duoInfoOpen, setDuoInfoOpen] = useState(false);
    async function refundThenUpdate(
        bookingId,
        oldTotal,
        newTotal,
        updatePayload
    ) {
        loader.start();
        try {
            const quoteResponse = await BookingService.editQuote(bookingId, updatePayload, axiosPrivate);
            const quote = quoteResponse?.data;
            await BookingService.refundDifference(
                {
                    old_total: quote?.old_net_total ?? oldTotal,
                    new_total: quote?.new_net_total ?? newTotal
                },
                bookingId,
                axiosPrivate
            );

            await BookingService.update(
                bookingId,
                updatePayload,
                axiosPrivate
            );
        } finally {
            loader.stop();
        }
    }

    const therapist1Info = useMemo(() => ({
        name: selectedTherapists[0]?.firstname,
        products: selectedProducts.filter(Boolean).filter((_, idx) => idx % 2 === 0),
    }), [selectedTherapists, selectedProducts]);

    const therapist2Info = useMemo(() => ({
        name: selectedTherapists[1]?.firstname,
        products: selectedProducts.filter(Boolean).filter((_, idx) => idx % 2 === 1),
    }), [selectedTherapists, selectedProducts]);

    const [removeIdx, setRemoveIdx] = useState(null);
    const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);

    const deleteTherapistAndProducts = async (idx) => {
        const nextTherapists = selectedTherapists.filter((_, i) => i !== idx);
        const nextProducts = selectedProducts.filter(Boolean).filter((_, pIdx) => (pIdx % 2) !== idx % 2);
        const isDuoNow = nextTherapists.length > 1;

        const oldTotal = price;
        const newTotal = nextProducts.reduce((s, p) => s + (p?.price || 0), 0);
        setConfirmDeleteOpen(false);
        setDuoInfoOpen(false);
        setIsProcessing(true);

        try {
            const updatePayload = {
                therapists: nextTherapists.map(therapist => therapist.id),
                products: nextProducts.map(product => product.id),
                isDuo: isDuoNow,
                refund_type: 'REFUND',
            };

            await prepareZoneAwareAction(
                updatePayload,
                nextTherapists,
                nextProducts,
                async confirmedPayload => {
                    await refundThenUpdate(
                        booking?.id,
                        oldTotal,
                        newTotal,
                        confirmedPayload
                    );

                    setSelectedTherapists(nextTherapists);
                    setSelectedProducts(nextProducts);
                    setPrice(newTotal);
                    setDuo(isDuoNow);
                    setCollapsedGuests(current => current.slice(0, nextProducts.length));
                    onUpdate?.({
                        ...booking,
                        therapists: nextTherapists,
                        products: nextProducts,
                        price: newTotal,
                    });
                    navigate("/tasks", { replace: true });
                    window.location.reload();
                }
            );
        } catch (err) {
            console.error('Unable to remove FlyPro from booking:', err);
            setErrorMessage(t('commande_edit:update_error'));
            setIsProcessing(false);
        }
    };

    const [productChangeModalOpen, setProductChangeModalOpen] = useState(false);
    const [priceDifference, setPriceDifference] = useState(0);
    const [productChangeDirection, setProductChangeDirection] = useState(null);
    const [isProcessingChange, setIsProcessingChange] = useState(false);
    const [showRefundSuccess, setShowRefundSuccess] = useState(false);

    const bookingDate = useMemo(() => {
        if (!booking?.start_before) return null;
        return new Date(booking.start_before.replace(' ', 'T'));
    }, [booking?.start_before]);

    const bookingTime = useMemo(() => {
        if (!bookingDate) return '';
        return bookingDate.toLocaleTimeString('en-US', {
            hour: '2-digit',
            minute: '2-digit',
            hour12: false
        });
    }, [bookingDate]);

    const navigate = useNavigate();
    const [selectedTherapist, setSelectedTherapist] = useState(selectedTherapists[0]);
    const [isEditingPrice, setIsEditingPrice] = useState(false);
    const [isEditingMessage, setIsEditingMessage] = useState(false);
    const [duoToggleRequested, setDuoToggleRequested] = useState(false);
    const [duoModalOpen, setDuoModalOpen] = useState(false);
    const [duoModalContentType, setDuoModalContentType] = useState('moreThan24h');
    const [showDisclaimerModal, setShowDisclaimerModal] = useState(false);
    const [domains, setDomains] = useState([]);
    const [showTherapistDisclaimer, setShowTherapistDisclaimer] = useState(false);
    const [availableTherapistSlots, setAvailableTherapistSlots] = useState([]);
    const [currentTherapistIsAvailable, setCurrentTherapistIsAvailable] = useState(false);
    const [otherTherapistsAvailable, setOtherTherapistsAvailable] = useState([]);
    const [availabilityChecked, setAvailabilityChecked] = useState(false);
    const [treeData, setTreeData] = useState([]);
    const [showProductTree, setShowProductTree] = useState(false);
    const [showSoinDisclaimer, setShowSoinDisclaimer] = useState(false);
    const [walleeScript, setWalleeScript] = useState(null);
    const [paymentUrl, setPaymentUrl] = useState(null);
    const [isPaymentModalOpen, setIsPaymentModalOpen] = useState(false);
    const [isProcessing, setIsProcessing] = useState(false);
    const [successMessage, setSuccessMessage] = useState('');
    const [errorMessage, setErrorMessage] = useState('');
    const therapistId = selectedTherapists[0]?.id; // already exists
    const selectedProductForCheck = selectedProducts[0];
    const [showRefundChoiceModal, setShowRefundChoiceModal] = useState(false);
    const [showProductConfirmModal, setShowProductConfirmModal] = useState(false);
    const [pendingUpdatePayload, setPendingUpdatePayload] = useState(null);
    const [showZoneOverrideConfirm, setShowZoneOverrideConfirm] = useState(false);
    const [previewZoneOverrideFlypros, setPreviewZoneOverrideFlypros] = useState([]);
    const [zoneOverrideFlypros, setZoneOverrideFlypros] = useState([]);
    const [pendingZoneOverridePayload, setPendingZoneOverridePayload] = useState(null);
    const pendingZoneOverrideActionRef = useRef(null);
    const availabilityRequestRef = useRef(0);

    const zurichTimee = new Date().toLocaleTimeString("en-US", {
        timeZone: "Europe/Zurich",
        hour: "2-digit",
        minute: "2-digit",
        hour12: false,
    });
    const [isRoom, setIsRoom] = useState(booking?.room_type ? booking.room_type === 'SPA_ROOM' ? false : true : false);

    const toggleRoomSwitch = () => {
        setIsRoom(!isRoom);
    };

    const evaluateTherapistSelection = async (
        therapistsToCheck = selectedTherapists,
        productsToCheck = selectedProducts
    ) => {
        const selected = (therapistsToCheck || []).filter(Boolean);
        const products = (productsToCheck || []).filter(Boolean);

        if (!booking?.id || !selectedDate || !selectedTime || !selected.length || !products.length) {
            throw new Error('Incomplete FlyPro availability data.');
        }

        const normalizedTime = /^\d{1,2}:\d{2}$/.test(selectedTime)
            ? selectedTime
            : '08:00';
        const { dateStr, timeStr } = getApiDateTime(selectedDate, normalizedTime, products[0]);
        const availabilityPayload = {
            booking_id: booking.id,
            date: dateStr,
            time: timeStr,
            duration: products.reduce((sum, product) => sum + Number(product?.duration || 0), 0),
            lng: booking.address?.longitude ?? bookingToEdit?.address?.longitude,
            lat: booking.address?.latitude ?? bookingToEdit?.address?.latitude,
            zip_code: booking.address?.zip_code ?? bookingToEdit?.address?.zip_code,
            products: products.map(product => product.id),
            is_duo: duo,
            room_type: isRoom ? 'HOTEL_ROOM' : 'SPA_ROOM',
            is_hotel: Boolean(booking.hotel),
            hotel_id: booking.hotel?.id,
            with: 'media,zones,categories,ratings,ratings.creator.media',
        };

        const normalResponse = await TherapistService.store(
            availabilityPayload,
            axiosPrivate
        );
        const normallyEligible = normalizeTherapistResponse(normalResponse);
        const missingSelected = classifyAdminZoneEligibility(
            selected,
            normallyEligible,
            normallyEligible
        ).invalid;

        let eligibleWithZoneOverride = normallyEligible;
        if (missingSelected.length) {
            const overrideResponse = await TherapistService.store(
                {
                    ...availabilityPayload,
                    zone_override_therapist_ids: missingSelected
                        .map(getFlyproId)
                        .filter(Boolean)
                        .map(Number),
                },
                axiosPrivate
            );
            eligibleWithZoneOverride = normalizeTherapistResponse(overrideResponse);
        }

        return {
            normallyEligible,
            eligibleWithZoneOverride,
            ...classifyAdminZoneEligibility(
                selected,
                normallyEligible,
                eligibleWithZoneOverride
            ),
        };
    };

    const selectedTherapistIdsSignature = selectedTherapists
        .filter(Boolean)
        .map(getFlyproId)
        .filter(Boolean)
        .join(',');
    const selectedProductIdsSignature = selectedProducts
        .filter(Boolean)
        .map(product => product.id)
        .join(',');

    useEffect(() => {
        const requestId = availabilityRequestRef.current + 1;
        availabilityRequestRef.current = requestId;
        setAvailabilityChecked(false);

        if (
            !booking?.id
            || !selectedDate
            || !selectedTime
            || !selectedTherapistIdsSignature
            || !selectedProductIdsSignature
        ) {
            setCurrentTherapistIsAvailable(false);
            setPreviewZoneOverrideFlypros([]);
            return;
        }

        const checkAvailability = async () => {
            loader.start();
            try {
                const result = await evaluateTherapistSelection();
                if (availabilityRequestRef.current !== requestId) return;

                const selectedIds = new Set(
                    selectedTherapists
                        .filter(Boolean)
                        .map(getFlyproId)
                        .filter(Boolean)
                );
                const others = result.normallyEligible.filter(
                    therapist => !selectedIds.has(getFlyproId(therapist))
                );

                setCurrentTherapistIsAvailable(result.invalid.length === 0);
                setPreviewZoneOverrideFlypros(result.outsideZone);
                setOtherTherapistsAvailable(others);
                setAvailableTherapistSlots(result.normallyEligible);
            } catch (error) {
                if (availabilityRequestRef.current !== requestId) return;
                console.error("Error checking therapist availability:", error);
                setCurrentTherapistIsAvailable(false);
                setPreviewZoneOverrideFlypros([]);
                setOtherTherapistsAvailable([]);
                setAvailableTherapistSlots([]);
            } finally {
                if (availabilityRequestRef.current === requestId) {
                    setAvailabilityChecked(true);
                }
                loader.stop();
            }
        };

        checkAvailability();
    }, [
        booking?.id,
        booking?.address?.longitude,
        booking?.address?.latitude,
        booking?.address?.zip_code,
        selectedDate,
        selectedTime,
        selectedTherapistIdsSignature,
        selectedProductIdsSignature,
        duo,
        isRoom,
    ]);

    const continueBookingUpdate = async (payload, context) => {
        const {
            dateChanged,
            productsChanged,
            newTotal,
        } = context;

        if (dateChanged) {
            setPendingBookingPayload(payload);
            setShowBookingConfirmationModal(true);
            setIsProcessing(false);
            return;
        }

        try {
            loader.start();
            const quoteResponse = await BookingService.editQuote(
                booking?.id,
                payload,
                axiosPrivate
            );
            const quote = quoteResponse?.data;

            if (productsChanged) {
                const quotedPayload = { ...payload, _edit_quote: quote };
                if (quote?.action && quote.action !== "UPDATE_ONLY") {
                    setPriceDifference(Math.abs(Number(quote.delta || 0)));
                    setProductChangeDirection(
                        quote.action === "PAY_DIFFERENCE" ? "increase" : "decrease"
                    );
                    setPendingBookingPayload(quotedPayload);
                    setProductChangeModalOpen(true);
                } else {
                    setPendingUpdatePayload(quotedPayload);
                    setShowProductConfirmModal(true);
                }
                setIsProcessing(false);
                return;
            }

            await BookingService.update(booking?.id, payload, axiosPrivate);
            onUpdate?.({
                ...booking,
                products: selectedProducts.filter(Boolean),
                product: !duo ? selectedProducts[0] : booking?.product,
                price: quote?.new_net_total ?? newTotal,
            });
            navigate("/tasks");
        } catch (error) {
            console.error("Booking update error:", error);
            setErrorMessage(
                error?.response?.data?.message || t('commande_edit:update_error')
            );
        } finally {
            loader.stop();
            setIsProcessing(false);
        }
    };

    const prepareZoneAwareAction = async (
        payload,
        therapistsToCheck,
        productsToCheck,
        action
    ) => {
        try {
            const zoneCoverage = classifySelectedZoneCoverage(
                therapistsToCheck,
                therapistsToCheck,
                booking?.address?.zip_code
            );

            if (zoneCoverage.unknown.length) {
                setErrorMessage(t('commande_edit:zone_override.check_failed'));
                setIsProcessing(false);
                return;
            }

            if (!zoneCoverage.outsideZone.length) {
                await action(payload);
                return;
            }

            const eligibility = await evaluateTherapistSelection(
                therapistsToCheck,
                productsToCheck
            );
            const overrideEligibility = classifyAdminZoneEligibility(
                zoneCoverage.outsideZone,
                [],
                eligibility.eligibleWithZoneOverride
            );

            if (overrideEligibility.invalid.length) {
                setCurrentTherapistIsAvailable(false);
                setErrorMessage(t('commande_edit:zone_override.not_eligible'));
                setIsProcessing(false);
                return;
            }

            if (overrideEligibility.outsideZone.length) {
                setCurrentTherapistIsAvailable(true);
                setPreviewZoneOverrideFlypros(overrideEligibility.outsideZone);
                setZoneOverrideFlypros(overrideEligibility.outsideZone);
                setPendingZoneOverridePayload(payload);
                pendingZoneOverrideActionRef.current = action;
                setShowZoneOverrideConfirm(true);
                setIsProcessing(false);
                return;
            }

            await action(payload);
        } catch (error) {
            console.error('FlyPro eligibility check failed:', error);
            setErrorMessage(t('commande_edit:zone_override.check_failed'));
            setIsProcessing(false);
        }
    };

    const handleZoneOverrideConfirm = async () => {
        const action = pendingZoneOverrideActionRef.current;
        if (!action || !pendingZoneOverridePayload || isProcessing) return;

        const payload = addAdminZoneOverride(
            pendingZoneOverridePayload,
            zoneOverrideFlypros
        );

        setShowZoneOverrideConfirm(false);
        setIsProcessing(true);
        try {
            await action(payload);
        } finally {
            pendingZoneOverrideActionRef.current = null;
            setPendingZoneOverridePayload(null);
            setZoneOverrideFlypros([]);
            setIsProcessing(false);
        }
    };

    const handleZoneOverrideCancel = () => {
        setShowZoneOverrideConfirm(false);
        setZoneOverrideFlypros([]);
        setPendingZoneOverridePayload(null);
        pendingZoneOverrideActionRef.current = null;
        setIsProcessing(false);
    };

    const handleUpdateBooking = async (e) => {
        e.preventDefault();
        if (isProcessing) return;

        setIsProcessing(true);
        setErrorMessage('');
        setSuccessMessage('');

        function toISODate(d) {
            if (!d) return null;
            if (typeof d === 'string') d = new Date(d.replace(' ', 'T'));
            return d.toISOString().slice(0, 10);
        }
        function toHM(t) {
            if (!t) return null;
            const parts = t.split(':');
            return `${parseInt(parts[0], 10)}:${parts[1].padStart(2, '0')}`;
        }

        const bookingDateObj = booking?.start_before
            ? new Date(booking.start_before.replace(' ', 'T'))
            : null;
        const originalDateStr = bookingDateObj
            ? bookingDateObj.toLocaleDateString('en-CA', { timeZone: 'Europe/Zurich' })
            : null;
        const originalTimeStr = bookingDateObj
            ? bookingDateObj.toLocaleTimeString('en-GB', {
                hour: '2-digit',
                minute: '2-digit',
                hour12: false,
                timeZone: 'Europe/Zurich',
            })
            : null;
        const selectedDateStr = toISODate(selectedDate);
        const selectedTimeStr = toHM(selectedTime);
        const newProductIds = selectedProducts.map(product => product?.id).filter(Boolean);
        const oldProductIds = (booking?.products ?? [booking?.product])
            .map(product => product?.id)
            .filter(Boolean);
        const newTherapistIds = selectedTherapists
            .filter(Boolean)
            .map(therapist => therapist.id);
        const oldTherapistIds = (booking?.therapists ?? [booking?.therapist])
            .map(therapist => therapist?.id)
            .filter(Boolean);
        const oldTotal = booking?.products?.reduce(
            (sum, product) => sum + (product?.price || 0),
            0
        ) || booking?.product?.price || 0;
        const newTotal = selectedProducts
            .filter(Boolean)
            .reduce((sum, product) => sum + (product?.price || 0), 0);
        const isEqual = (a, b) => JSON.stringify(a) === JSON.stringify(b);
        const dateChanged = originalDateStr !== selectedDateStr
            || originalTimeStr !== selectedTimeStr;
        const productsChanged = !isEqual(oldProductIds, newProductIds);

        if (
            !productsChanged
            && isEqual(oldTherapistIds, newTherapistIds)
            && !dateChanged
            && (booking?.message || '') === (message || '')
            && booking?.isDuo === duo
            && oldTotal === newTotal
        ) {
            setIsProcessing(false);
            return;
        }

        const { full } = getApiDateTime(selectedDate, selectedTime);
        const payload = {
            products: newProductIds,
            product_id: !duo ? newProductIds[0] : undefined,
            therapists: newTherapistIds,
            start_before: full,
            date: full,
            isDuo: duo,
            room_type: isRoom ? 'HOTEL_ROOM' : 'SPA_ROOM',
            message,
            duration: Math.round(
                selectedProducts
                    .filter(Boolean)
                    .reduce((sum, product) => sum + parseInt(product?.duration || 0, 10), 0)
            ),
            address_id: booking?.address?.id,
            zip_code: booking?.address?.zip_code,
            city: booking?.address?.city,
            street: booking?.address?.street,
            street_number: booking?.address?.streetnumber || booking?.address?.street_number,
            state: booking?.address?.state,
            state_code: booking?.address?.state_code,
            country: booking?.address?.country,
            country_code: booking?.address?.country_code,
            longitude: booking?.address?.longitude,
            latitude: booking?.address?.latitude,
        };
        const context = { dateChanged, productsChanged, newTotal };

        await prepareZoneAwareAction(
            payload,
            selectedTherapists,
            selectedProducts,
            confirmedPayload => continueBookingUpdate(confirmedPayload, context)
        );
    };

    const getMonthName = (date) => date.toLocaleString('fr-FR', { month: 'long' });
    const getDayName = (date) => date.toLocaleString('fr-FR', { weekday: 'short' });

    const getInitialDaysToShow = (baseDate) => {
        if (!baseDate || isNaN(baseDate.getTime())) return [];
        const days = [];
        days.push(new Date(baseDate));
        for (let i = 1; i <= 4; i++) {
            const prevDay = new Date(baseDate);
            prevDay.setDate(baseDate.getDate() - i);
            days.unshift(prevDay);
            const nextDay = new Date(baseDate);
            nextDay.setDate(baseDate.getDate() + i);
            days.push(nextDay);
        }
        return days.slice(0, 9);
    };


    const [daysToShow, setDaysToShow] = useState(() => getInitialDaysToShow());
    const updateDaysToShow = (baseDate) => {
        const days = [];
        days.push(new Date(baseDate));

        for (let i = 1; i <= 4; i++) {
            const prevDay = new Date(baseDate);
            prevDay.setDate(baseDate.getDate() - i);
            days.unshift(prevDay);

            const nextDay = new Date(baseDate);
            nextDay.setDate(baseDate.getDate() + i);
            days.push(nextDay);
        }

        setDaysToShow(days.slice(0, 9));
    };


    const getNextHourSlots = (date) => {
        const now = new Date();
        const hours = [];
        if (date?.toDateString() === now?.toDateString()) {
            const minutes = now.getMinutes();
            const nextQuarterHour = Math.ceil(minutes / 15) * 15;
            now.setMinutes(nextQuarterHour, 0);
            for (let i = 0; i < 57; i++) {
                const slot = new Date(now.getTime() + i * 15 * 60000);
                if (slot.getHours() >= 8 && slot.getHours() < 22) {
                    hours.push(formatTimeHi(slot.toLocaleTimeString('fr-FR', {
                        timeZone: "Europe/Zurich",
                        hour: "2-digit",
                        minute: "2-digit",
                        hour12: false,
                    })));
                }
            }
        } else {
            const startOfDay = new Date(date);
            startOfDay.setHours(8, 0, 0, 0);
            for (let i = 0; i < 57; i++) {
                const slot = new Date(startOfDay.getTime() + i * 15 * 60000);
                if (slot.getHours() >= 8 && slot.getHours() < 22) {
                    hours.push(formatTimeHi(slot.toLocaleTimeString('fr-FR', {
                        timeZone: "Europe/Zurich",
                        hour: "2-digit",
                        minute: "2-digit",
                        hour12: false,
                    })));
                }
            }
        }
        return hours;
    };

    const hours = getNextHourSlots(selectedDate);

    const handleMonthChange = (direction) => {
        const newDate = new Date(selectedDate);
        newDate.setMonth(newDate.getMonth() + direction);
        setSelectedDate(newDate);
        updateDaysToShow(newDate);
    };
    const handleDayChange = (direction) => {
        const newDate = new Date(selectedDate);
        newDate.setDate(selectedDate.getDate() + direction);
        setSelectedDate(newDate);
        setDaysToShow(getInitialDaysToShow(newDate));
    };
    const scrollLeft = () => {
        const container = document.querySelector('.row-day');
        container?.scrollBy({ left: -200, behavior: 'smooth' });
    };

    const scrollRight = () => {
        const container = document.querySelector('.row-day');
        container?.scrollBy({ left: 200, behavior: 'smooth' });
    };

    const [categoryProducts, setCategoryProducts] = useState([]);

    const confirmAddressChange = () => {
        // Save current editing state
        sessionStorage.setItem("modificationBookingId", bookingToEdit.id);
        sessionStorage.setItem("editBookingMode", "true");
        sessionStorage.setItem("editAddressMode", "true");
        sessionStorage.setItem("bookingToEdit", JSON.stringify(bookingToEdit));
        sessionStorage.setItem("oldBooking", JSON.stringify(bookingToEdit));

        const therapists = selectedTherapists.filter(Boolean);

        const addressObj = booking?.address || bookingToEdit.address || {};

        const statePayload = {
            address: {
                id: addressObj.id ?? null,
                street: addressObj.street || "",
                zip_code: addressObj.zip_code || "",
                city: addressObj.city || addressObj.state || "",
                state: addressObj.state || "",
                country: addressObj.country || "",
                street_number: addressObj.street_number || addressObj.streetnumber || "",
                state_code: addressObj.state_code || "",
                country_code: addressObj.country_code || "",
                lat: addressObj.latitude ?? addressObj.lat ?? null,
                lng: addressObj.longitude ?? addressObj.lng ?? null,
                phone: addressObj.phone || "",
            },
            phone: addressObj.phone || "",
            selectedServices: bookingToEdit.services || [],
            duo: bookingToEdit.isDuo,
            room: bookingToEdit.room_type,
            selectedTherapists: therapists,
        };

        sessionStorage.setItem("state", JSON.stringify(statePayload));
        const oldBookingWithTime = { ...bookingToEdit, time: selectedTime };
        sessionStorage.setItem("oldBooking", JSON.stringify(oldBookingWithTime));
        const redirect = flowType === "pro" ? "/location-pro" : "/choose-location";
        navigate(redirect, { state: { fromEditBooking: true } });
    };

    useEffect(() => {
        const fetchDomains = async () => {
            try {
                loader.start();
                const response = await DomainService.index(
                    { with: 'media,zones', sort: 'ordering' },
                    axiosPrivate,
                    !auth?.accessToken
                );
                setDomains(response);
            } catch (err) {
                console.error("Error fetching domains:", err);
            } finally {
                loader.stop();
            }
        };
        fetchDomains();
    }, []);

    useEffect(() => {
        const fetchProductsForCategory = async () => {
            if (product?.category_id) {
                try {
                    loader.start();
                    const response = await ProductService.index(
                        {
                            is_hidden: false,
                            is_active: 1,
                            category_id: product.category_id,
                            with: 'media,category'
                        },
                        axiosPrivate
                    );
                    if (response?.length) {
                        setCategoryProducts(response);
                    } else {
                        console.warn("No products found for category (subcategory) ID", product.category_id);
                    }
                } catch (error) {
                    console.error("Error fetching products:", error);
                } finally {
                    loader.stop();
                }
            }
        };
        fetchProductsForCategory();
    }, [product?.category_id]);

    const buildCategoryProductsList = async (products, axios) => {
        return Promise.all(
            products.map(async (prod) => {
                if (!prod) return [];

                const catId = prod.category_id ?? prod.category?.id;
                let list = [];

                if (catId) {
                    list = await ProductService.index(
                        {
                            is_active: 1,
                            category_id: catId,
                            with: "media,category,domain",
                        },
                        axios
                    );
                }

                if (!list.some((p) => p.id === prod.id)) {
                    list.unshift(prod);
                }
                // Ordered by duration
                list.sort((a, b) => Number(a.duration) - Number(b.duration));

                return list;
            })
        );
    };

    useEffect(() => {
        if (!selectedProducts.length) return;

        buildCategoryProductsList(selectedProducts, axiosPrivate)
            .then((lists) => setCategoryProductsList(lists))
            .catch((err) => console.error("Error fetching category products:", err));
    }, [
        duo,
        selectedProducts.map((p) => p?.id).join(","),
    ]);

    useEffect(() => {
        if (!booking) return;

        async function hydrateAndFetch() {
            loader.start();
            try {
                const bookingProducts = booking.products ?? [];
                const hydrated = await Promise.all(
                    bookingProducts.map(prod => hydrateProduct(prod, axiosPrivate))
                );

                const padded = [...hydrated, ...Array(4 - hydrated.length).fill(null)].slice(0, 4);
                setSelectedProducts(padded);
                setPrice(padded.reduce((sum, p) => sum + (p?.price || 0), 0));
                setDuo(booking.isDuo);
                setIsRoom(booking.room_type ? booking.room_type === 'SPA_ROOM' ? false : true : false)
            } finally {
                loader.stop();
            }
        }
        hydrateAndFetch();
    }, [booking?.id]);

    useEffect(() => {
        const scrollToSelectedHour = () => {
            const button = document.querySelector(`.btn-hour.selected`);
            if (button) {
                button.scrollIntoView({ behavior: 'smooth', inline: 'center', block: 'nearest' });
            }
        };

        const scrollToSelectedDay = () => {
            const button = document.querySelector(`.day.selected`);
            if (button) {
                button.scrollIntoView({ behavior: 'smooth', inline: 'center', block: 'nearest' });
            }
        };

        setTimeout(scrollToSelectedHour, 100);
    }, [selectedTime, selectedDate, product, selectedProducts[0], axiosPrivate, auth]);

    const confirmTherapistChange = () => {
        // Enable edit‑mode
        sessionStorage.setItem("modificationBookingId", String(booking?.id));
        sessionStorage.setItem("editBookingMode", "true");

        const dateStr = selectedDate.toISOString().split("T")[0];

        // old vs new
        const oldBooking = {
            id: booking?.id,
            date: dateStr,
            time: selectedTime,
            products: booking?.products ?? [booking?.product],
            therapists: booking?.therapists ?? [booking?.therapist],
            address: booking?.address,
            isDuo: booking?.isDuo,
            room_type: booking?.room_type,
        };
        const newBooking = {
            ...oldBooking,
            therapists: selectedTherapists.filter(Boolean).slice(0, 2),
            isDuo: duo,
            room_type: isRoom ? 'HOTEL_ROOM' : 'SPA_ROOM',
        };

        sessionStorage.setItem("oldBooking", JSON.stringify(oldBooking));
        sessionStorage.setItem("newBooking", JSON.stringify(newBooking));
        sessionStorage.setItem("bookingToEdit", JSON.stringify(bookingToEdit));

        // Prepare the state
        const fullAddress = {
            id: booking?.address?.id ?? null,
            street: booking?.address?.street || "",
            street_number:
                booking?.address?.streetnumber ||
                booking?.address?.street_number ||
                "",
            city: booking?.address?.city || booking?.address?.state || "",
            zip_code: booking?.address?.zip_code || "",
            state: booking?.address?.state || "",
            state_code: booking?.address?.state_code || "",
            country: booking?.address?.country || "",
            country_code: booking?.address?.country_code || "",
            lat: booking?.address?.latitude || booking?.address?.lat || null,
            lng: booking?.address?.longitude || booking?.address?.lng || null,
        };

        const selectedServicesPayload = selectedProducts
            .filter(Boolean)
            .map(prod => ({
                product: prod,
            }));

        const followUpState = {
            address: fullAddress,
            phone: booking?.address?.phone || "",
            selectedServices: selectedServicesPayload,
            selectedTherapist: selectedTherapists.filter(Boolean).slice(0, 2),
            duo,
            date: dateStr,
            time: selectedTime ? selectedTime
            .split(':')
            .map((part, i) => (i === 0 ? part.padStart(2, '0') : part))
            .join(':') : '',
            origin_flow: booking?.origin_flow,
        };
        sessionStorage.setItem("state", JSON.stringify(followUpState));

        // passing selectedTherapist in navigation state
        setShowTherapistDisclaimer(false);
        const redirectPath = flowType === "pro" ? "/location-pro" : "/choose-flypros";
        navigate('/choose-flypros', {
            state: {
                fromEditBooking: true,
                selectedTherapist: selectedTherapists,
                selectedServices: selectedServicesPayload,
                duo,
                desc: booking.message,
            }
        });
    };

    const originallyDuo = useRef(undefined);

    useEffect(() => {
        if (booking && originallyDuo.current === undefined) {
            originallyDuo.current = booking.isDuo;
        }
    }, [booking]);

    const calculateTimeDifference = (startTime) => {
        const now = new Date();
        const start = new Date(startTime);
        const timeDiff = start.getTime() - now.getTime();
        const hoursDiff = timeDiff / (1000 * 3600);
        return hoursDiff;
    };

    const handleDuoToggle = () => {
        if (originallyDuo.current) {
            setDuoInfoOpen(true);
        } else {
            const hoursDiff = calculateTimeDifference(booking.start_before);
            const isMoreThan24h = hoursDiff > 24;

            setDuoModalOpen(true);
            setDuoModalContentType(isMoreThan24h ? 'moreThan24h' : 'lessThan24h');
            setDuoToggleRequested(true);
        }
    };

    const confirmSoinChange = async () => {
        // Prepare the full address
        const address = booking?.address || {};
        const fullAddress = {
            id: address.id,
            street: address.street,
            city: address.city ?? address.state,
            zip_code: address.zip_code,
            street_number: address.streetnumber || address.street_number || '',
            state: address.state,
            state_code: address.state_code,
            country: address.country,
            country_code: address.country_code,
            lat: address.latitude,
            lng: address.longitude,
        };

        // For each selected product, fetch the category & domain
        const selectedServices = await Promise.all(
            selectedProducts.filter(Boolean).map(async prod => {
                const { category, domain } = await fetchCategoryAndDomain(prod, axiosPrivate);
                return {
                    product: {
                        id: prod?.id,
                        name: prod?.name,
                        duration: prod?.duration,
                        price: prod?.price,
                        ...prod,
                    },
                    category,
                    domain,
                };
            })
        );

        // Create old and new booking objects to store in session storage
        const oldBooking = {
            id: booking?.id,
            date: selectedDate.toISOString().split("T")[0],
            time: selectedTime,
            products: booking?.products ?? [booking?.product],
            therapists: booking?.therapists ?? [booking?.therapist],
            address: booking?.address,
            price: booking?.products?.reduce((sum, p) => sum + (p?.price || 0), 0) || booking?.product?.price || 0,
            isDuo: booking?.isDuo,
        };

        const newBooking = {
            ...oldBooking,
            products: selectedProducts.filter(Boolean),
            time: selectedTime,
            therapists: selectedTherapists,
            price: selectedProducts.filter(Boolean).reduce((sum, p) => sum + (p?.price || 0), 0),
            isDuo: duo,
        };

        // Save the old and new booking details in session storage for comparison later
        sessionStorage.setItem("modificationBookingId", booking?.id);
        sessionStorage.setItem("editBookingMode", "true");
        sessionStorage.setItem("oldBooking", JSON.stringify(oldBooking));
        sessionStorage.setItem("newBooking", JSON.stringify(newBooking));
        sessionStorage.setItem("productChanged", "true");

        console.log("Saving to sessionStorage", selectedServices);

        // Store prefilled state for /choose-products
        sessionStorage.setItem("state", JSON.stringify({
            address: fullAddress,
            phone: address.phone ?? "",
            selectedServices,
            duo: duo,
            room: isRoom ? 'HOTEL_ROOM' : 'SPA_ROOM',
            selectedDate: selectedDate?.toISOString() ?? null,
            selectedTime,
            message: message ?? '',
            origin_flow: booking?.origin_flow,
        }));

        setShowSoinDisclaimer(false);

        // Redirect directly to choose-products
        navigate("/choose-products", {
            state: {
                address: fullAddress,
                phone: address.phone ?? "",
                selectedServices,
                duo: duo,
                room: isRoom ? 'HOTEL_ROOM' : 'SPA_ROOM',
                selectedDate: selectedDate?.toISOString() ?? null,
                selectedTime,
                message: message ?? '',
                origin_flow: booking?.origin_flow,
                fromEditBooking: true,
            }
        });
    };

    const showWalleeLightbox = (scriptUrl, paymentUrl, onSuccess) => {
        // Si le handler existe déjà, on démarre directement le loader et la lightbox
        if (window.LightboxCheckoutHandler) {
            loader.start();
            window.LightboxCheckoutHandler.startPayment(paymentUrl, () => {
                loader.stop();
                onSuccess();
            });
            return;
        }

        // Sinon, on télécharge le script Wallee en affichant le loader
        loader.start();
        const script = document.createElement('script');
        script.src = scriptUrl;
        script.async = true;
        script.onload = () => {
            // Une fois le script chargé, on démarre la lightbox
            window.LightboxCheckoutHandler.startPayment(paymentUrl, () => {
                loader.stop();
                onSuccess();
            });
        };
        document.body.appendChild(script);
    };

    function formatDuration(min) {
        if (!min) return "--";
        const h = Math.floor(min / 60);
        const m = min % 60;
        return m
            ? `${h}h ${m} min`
            : `${h}h`;
    }
    const getCatTitle = p => p?.category?.title ?? '';

    const handleProductRefund = async () => {
        setShowRefundChoiceModal(false);
        setProductChangeModalOpen(false);
        loader.start();
        try {
            const { _edit_quote, ...updatePayload } = pendingBookingPayload;
            // refund
            await BookingService.refundDifference(
                { old_total: _edit_quote?.old_net_total, new_total: _edit_quote?.new_net_total },
                booking?.id,
                axiosPrivate
            );
            updatePayload.refund_type = 'REFUND';
            // Update booking
            await BookingService.update(
                booking?.id,
                updatePayload,
                axiosPrivate
            );
            notify.success(t('commande_edit:refund_processing', { amount: priceDifference }));            
            navigate("/commande");
        } catch (err) {

        } finally {
            loader.stop();
        }
    };

    useEffect(() => {
        const fetchAvailableHours = async () => {
            if (
                !booking ||
                !booking.address ||
                !selectedDate ||
                (!Array.isArray(booking.therapists) && !booking.therapist) ||
                !selectedProducts.length
            ) {
                return;
            }

            const productIds = selectedProducts.map(p => p?.id).filter(Boolean);
            const totalDuration = selectedProducts.reduce((sum, p) => sum + Number((p?.duration || 0)), 0);
            const therapistIds = booking.therapists
                ? booking.therapists.map(t => t.id)
                : booking.therapist
                    ? [booking.therapist.id]
                    : [];

            if (!therapistIds.length || !productIds.length) return;

            console.log('Fetching available hours for:', {
                date: new Date(selectedDate).toLocaleDateString('en-CA').split('T')[0],
                duration: totalDuration,
                lng: booking.address.longitude,
                lat: booking.address.latitude,
                zip_code: booking.address.zip_code,
                products: productIds,
                therapists: therapistIds,
                is_hotel: true,
                hotel_id: booking.hotel?.id,
                booking_id: booking.id,
                is_duo: duo,
            });

            try {
                const availabilities = await AvailableSlotsService.store(
                    {
                        date: new Date(selectedDate).toLocaleDateString('en-CA').split('T')[0],
                        duration: totalDuration,
                        lng: booking.address.longitude,
                        lat: booking.address.latitude,
                        zip_code: booking.address.zip_code,
                        products: productIds,
                        therapists: therapistIds,
                        is_hotel: !!booking.hotel,
                        hotel_id: booking.hotel?.id,
                        booking_id: booking.id,
                        is_duo: duo,
                        room_type: isRoom ? 'HOTEL_ROOM' : 'SPA_ROOM',
                    },
                    axiosPrivate
                );
                setAvailableHours(availabilities);
                console.log('Available hours fetched:', availabilities);
                console.log('availableHoursssssssssssss', availableHours);
            } catch (error) {
                console.error('Error fetching available hours:', error);
            }
        };

        fetchAvailableHours();
    }, [
        selectedDate,
        selectedTime,
        selectedProducts,
    ]);


    return (
        <>
            <MenuHeader />
            <div className="content-wrapper">
                <div className="content-wrapper duo-pay">
                    <div className='commande-edit'>
                        <form className="form listItemWithImageParent" onSubmit={handleUpdateBooking}>
                            {/* Header/Nav Section */}
                            <div
                                className="edit-commande-header"
                                style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'space-between',
                                    width: '100%',
                                    position: 'relative',
                                    background: 'transparent',
                                    marginTop: '-60px',
                                }}
                            >
                                <div style={{ flex: 2, display: 'flex', alignItems: 'center', marginLeft: '-150px' }}>
                                    <Button
                                        color="dark"
                                        sx={{
                                            textTransform: 'none',
                                            fontSize: '18px',
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: '6px',
                                            padding: 0,
                                            minWidth: 0
                                        }}
                                        onClick={handleBack}
                                    >
                                        <img src="/assets/images/icon-back.svg" alt="Retour" className="w-4 h-4" />
                                        {t('commande_edit:back')}
                                    </Button>
                                </div>
                                <div style={{ flex: 1, textAlign: 'center' }}>
                                    <h2 className="text-2xl font-semibold" style={{ margin: 0 }}>{t('commande_edit:edit_order')}</h2>
                                </div>
                                <div style={{ flex: 1 }}></div>
                            </div>

                            {/* Therapist */}
                            {duo ? (
                                <div className="therapist-duo-container margin-top-min-30">
                                    {selectedTherapists
                                        .filter(Boolean)
                                        .slice(0, 2)
                                        .map((therapist, idx) => (
                                            <div key={therapist.id ?? idx} className="listItemWithImage">
                                                <img
                                                    className="imageIcon"
                                                    alt=""
                                                    src={therapist.media?.[0]?.original_url || user}
                                                />
                                                <div className="text">
                                                    <div className="title top-min-15">
                                                        <div className="kierra">
                                                            {therapist.firstname || `${t('commande_edit:person')} ${idx + 1}`}
                                                        </div>
                                                        <div className="rating">
                                                            <div className="stars">
                                                                <div className="starSharp">★</div>
                                                            </div>
                                                            <div className="div">
                                                                {therapist.extra?.rating?.toFixed(1) || '5.0'}/5
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                                <EditIcon
                                                    style={{ fontSize: 18, color: '#333' }}
                                                    onClick={() => setShowTherapistDisclaimer(true)}
                                                />
                                            </div>
                                        ))}
                                </div>
                            ) : (
                                console.log(selectedTherapists),
                                <div className="listItemWithImage margin-top-min-30">
                                    <img
                                        className="imageIcon"
                                        alt=""
                                        src={selectedTherapists[0]?.media?.[0]?.original_url || user}
                                    />
                                    <div className="text">
                                        <div className="title top-min-15">
                                            <div className="kierra">{selectedTherapists[0]?.firstname}</div>
                                            <div className="rating">
                                                <div className="stars">
                                                    <div className="starSharp">★</div>
                                                </div>
                                                <div className="div">
                                                    {selectedTherapists[0]?.extra?.rating?.toFixed(1) || '5.0'}/5
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                    <EditIcon
                                        style={{ fontSize: 18, color: '#333' }}
                                        onClick={() => setShowTherapistDisclaimer(true)}
                                    />
                                </div>
                            )}

                            {/* Massage DUO toggle */}
                            {originFlow !== 'pro' && (
                                <div className="duo-toggle-row margin-top-min-30">
                                    <span className="duo-toggle-row__label">{t('commande_edit:duo_massage')}</span>
                                    <label className="switch">
                                        <input
                                            type="checkbox"
                                            checked={duo}
                                            onChange={handleDuoToggle}
                                        />
                                        <span className="slider round" />
                                    </label>
                                </div>
                            )}

                            <div className="duo-toggle-row margin-top-min-30">
                                <span className="duo-toggle-row__label">{t('service:salle_massage')} </span>

                                <label className="switch">
                                    <input
                                        type="checkbox"
                                        checked={!isRoom}
                                        onChange={toggleRoomSwitch}
                                    />
                                    <span className="slider round" />
                                </label>

                                <span className="duo-toggle-row__label">{t('service:chambre_massage')} </span>

                                <label className="switch">
                                    <input
                                        type="checkbox"
                                        checked={isRoom}
                                        onChange={toggleRoomSwitch}
                                    />
                                    <span className="slider round" />
                                </label>
                            </div>


                            {/* SOLO */}
                            {!duo && categoryProductsList[0]?.length < 2 && (
                                <div className="duration-tabs margin-top-min-30">
                                    {categoryProductsList[0].map(prod => (
                                        <button
                                            key={prod.id}
                                            type="button"
                                            className={`duration-tab ${selectedProducts[0]?.id === prod.id ? 'duration-tab--active' : ''
                                                }`}
                                            onClick={() => {
                                                setSelectedProducts([prod]);
                                                setPrice(prod.price);
                                            }}
                                        >
                                            <span className="duration-tab__minutes">{prod.duration}&nbsp;min</span>
                                            <span className="duration-tab__price">{prod.price}&nbsp;chf</span>
                                        </button>
                                    ))}
                                </div>
                            )}

                            {/* DUO – Invité(e) */}
                            {duo || categoryProductsList[0]?.length >= 2 && (
                                <div className="guest-list margin-top-min-30">
                                    {selectedProducts
                                        .map((prod, idx) => ({ prod, idx }))
                                        .filter(({ prod }) => prod)
                                        .map(({ prod, idx }) => (
                                            <GuestCard
                                                key={idx}
                                                index={idx}
                                                product={prod}
                                                durations={categoryProductsList[idx] || []}
                                                collapsed={collapsedGuests[idx]}
                                                onToggle={toggleGuest}
                                                onSelectDuration={(i, newProd) => {
                                                    const updated = [...selectedProducts];
                                                    updated[i] = newProd;
                                                    setSelectedProducts(updated);
                                                    setPrice(updated.reduce((s, p) => s + (p?.price || 0), 0));
                                                }}
                                            />
                                        ))}
                                </div>
                            )}

                            {/* DateTimeSelector */}
                            <div className="form-masseur margin-top-min-30" style={{ margin: 0, padding: 0 }}>
                                <DateTimeSelector
                                    selectedDate={selectedDate}
                                    setSelectedDate={setSelectedDate}
                                    selectedTime={selectedTime}
                                    setSelectedTime={setSelectedTime}
                                    totalDuration={duration}
                                    daysToShow={daysToShow}
                                    hours={hours}
                                    getMonthName={getMonthName}
                                    getDayName={getDayName}
                                    updateDaysToShow={updateDaysToShow}
                                    modalIsOpen2={false}
                                    handleMonthChange={handleMonthChange}
                                    handleDayChange={handleDayChange}
                                    handleDayClick={(date) => setSelectedDate(date)}
                                    handleHourClick={(hour) => setSelectedTime(hour)}
                                    scrollLeft={scrollLeft}
                                    scrollRight={scrollRight}
                                    unavailableDates={daysToShow.map((d) => d.toDateString())}
                                    availableHours={availableHours}
                                    buffertime={{ bufferBefore: 0, bufferAfter: 0 }}
                                    totalPrice={price}
                                    openModal={() => { }}
                                    getFilterText={""}
                                    handleResetFilter={() => { }}
                                    isEditMode={true}
                                />

                            </div>

                            <div className="margin-top-min-30" style={{ margin: '8px 0 0 0', padding: 0, minHeight: 24 }}>
                                {availabilityChecked && (
                                    <>
                                        {!currentTherapistIsAvailable ? (
                                            <p>🚫 {t('commande_edit:none')}</p>
                                        ) : (
                                            <>
                                                <p>✅ {t('commande_edit:therapist_available')}</p>
                                                {previewZoneOverrideFlypros.length > 0 && (
                                                    <p style={{ color: '#9a6700' }}>
                                                        ⚠️ {t('commande_edit:zone_override.inline_warning', {
                                                            flypros: previewZoneOverrideFlypros
                                                                .map(getFlyproLabel)
                                                                .join(', '),
                                                        })}
                                                    </p>
                                                )}
                                            </>
                                        )}
                                    </>
                                )}
                            </div>

                            <div className="massage-type-block margin-top-min-30">
                                <p style={{ marginBottom: 6, fontWeight: 500 }}>Soin type&nbsp;:</p>
                                <div
                                    className="massage-type-field"
                                    onClick={() => {
                                        if (selectedProducts.filter(Boolean).length > 1) {
                                            setProductDropdownOpen(!productDropdownOpen);
                                        }
                                    }}
                                    role="button"
                                >
                                    <span className="massage-type-field__label">
                                        {duo
                                            ? firstProd
                                                ? `${getCatTitle(firstProd)} – ${firstProd.name} – `
                                                + `${firstProd.duration}\u202Fmin – ${firstProd.price}\u202FCHF`
                                                : t('commande_edit:select_massage')

                                            : firstProd
                                                ? `${getCatTitle(firstProd)} – ${firstProd.name} – `
                                                + `${firstProd.duration}\u202Fmin`
                                                : t('commande_edit:select_massage')}
                                    </span>
                                    <div style={{ gap: '25px', display: 'flex' }}>
                                        {translatedProducts.filter(Boolean).length > 1 && (
                                            <KeyboardArrowDownIcon
                                                className="guest-card__chevron"
                                                style={{
                                                    transform: productDropdownOpen ? 'rotate(-90deg)' : 'rotate(90deg)',
                                                    transition: 'transform 0.2s ease',
                                                    fontSize: 25
                                                }}
                                            />
                                        )}
                                        <EditIcon
                                            style={{ fontSize: 23, color: '#333', cursor: 'pointer' }}
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                setShowSoinDisclaimer(true);
                                            }}
                                        />
                                    </div>
                                </div>

                                {productDropdownOpen && (
                                    <div className="form-field">
                                        {translatedProducts.filter(Boolean).slice(1).map((prod, idx, arr) => (
                                            <React.Fragment key={prod.id}>
                                                <div className="massage-type-field">
                                                    <span className="massage-type-field__label">
                                                        {getCatTitle(prod)} –{' '}
                                                        {prod.name} –{' '}
                                                        {prod.duration} min – {prod.price} CHF
                                                    </span>
                                                </div>
                                                {idx !== arr.length - 1 && <div className="form-field-child" />}
                                            </React.Fragment>
                                        ))}
                                    </div>
                                )}

                            </div>

                            {/* Duration / Total row  */}
                            <div className="commande-edit duration-total-row margin-top-min-30">
                                <span className="duration-label">
                                    {t('commande_edit:duration_label')}{formatDuration(duration)}
                                </span>

                                <span className="total-price">
                                    {selectedProducts.filter(Boolean).reduce((s, p) => s + (p.price || 0), 0)} CHF
                                </span>
                            </div>

                            {/* Address */}
                            {/* <div className="address-block margin-top-min-30">
                                <p className="address-label">Address</p>
                                <div className="form-field address-field">
                                    <div className="rue-de-geneva-container">
                                        <p className="rue-de-geneva">
                                            {booking?.address?.street} {booking?.address?.street_number}
                                        </p>
                                        <p className="rue-de-geneva">
                                            {booking?.address?.zip_code} {booking?.address?.state}
                                        </p>

                                        <p className="rue-de-geneva">{booking?.phone}</p>

                                    </div>
                                    <EditIcon
                                        style={{ fontSize: 18, color: '#333', cursor: 'pointer' }}
                                        onClick={() => setShowDisclaimerModal(true)}
                                    />
                                </div>
                            </div> */}

                            {/* Message au Flypro  */}
                            <div className="flypro-message-block margin-top-min-30">
                                <p className="flypro-message-label">{t('commande_edit:message_to_flypro_label')}</p>
                                <div className="form-field message-field" onClick={() => !isEditingMessage && setIsEditingMessage(true)}>

                                    {isEditingMessage ? (
                                        <textarea
                                            className="message-textarea"
                                            value={message}
                                            onChange={(e) => setMessage(e.target.value)}
                                            onBlur={() => setIsEditingMessage(false)}
                                            placeholder={t('commande_edit:example_message')}
                                            rows={3}
                                            autoFocus
                                        />
                                    ) : (
                                        <p className="message-placeholder">
                                            {message || t('commande_edit:example_message')}
                                        </p>
                                    )}

                                    {/* pencil */}
                                    <EditIcon
                                        style={{ fontSize: 18, color: '#333', cursor: 'pointer' }}
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            setIsEditingMessage(true);
                                        }}
                                    />
                                </div>
                            </div>

                            {showDisclaimerModal && (
                                <Modal
                                    isOpen={showDisclaimerModal}
                                    onRequestClose={() => setShowDisclaimerModal(false)}
                                    style={{
                                        content: {
                                            width: "90%",
                                            maxWidth: "420px",
                                            maxHeight: "420px",
                                            margin: "auto",
                                            padding: "30px",
                                            borderRadius: "12px",
                                            textAlign: "center",
                                        }
                                    }}
                                >
                                    <h2>{t('commande_edit:change_address')}</h2>
                                    <p>
                                        {t('commande_edit:address_change_disclaimer')}<br />
                                        {t('commande_edit:address_change_disclaimer2')}<br />
                                        {t('commande_edit:address_change_disclaimer3')}
                                    </p>
                                    <div className="modal-buttons">
                                        <button onClick={() => setShowDisclaimerModal(false)} className="btn btn-secondary">
                                            {t('commande_edit:cancel')}
                                        </button>
                                        <button onClick={confirmAddressChange} className="btn btn-primary">
                                            {t('commande_edit:continue')}
                                        </button>
                                    </div>
                                </Modal>
                            )}

                            {/* Submit Button */}
                            <div className="rectangleDiv">
                                <button
                                    type="submit"
                                    className="btn bg-[#2D2D2D] text-[#FFD700] text-sm px-8 py-3 rounded-xl shadow-md hover:bg-[#1f1f1f] transition duration-200"
                                    disabled={isProcessing}
                                >
                                    {t('commande_edit:update_order')}
                                </button>
                            </div>
                        </form>

                    </div>
                </div>
            </div>
            <Modal
                isOpen={duoModalOpen}
                onRequestClose={() => setDuoModalOpen(false)}
                style={{
                    content: {
                        width: "90%",
                        maxWidth: "420px",
                        maxHeight: "350px",
                        margin: "auto",
                        padding: "30px",
                        borderRadius: "12px",
                        textAlign: "center",
                    }
                }}
            >
                <h2> {t('commande_edit:sorry')} !</h2>
                {duoModalContentType === 'moreThan24h' ? (
                    // More than 24h content
                    <>
                        <p>
                            {t('commande_edit:duo_massage_disclaimer')}
                            <br /><br />
                            {t('commande_edit:duo_massage_disclaimer2')}
                        </p>
                    </>
                ) : (
                    // Less than 24h content
                    <>
                        <p>
                            {t('commande_edit:duo_massage_disclaimer')}
                            <br /><br />
                            {t('commande_edit:call_flypsa')}
                            <br />
                            +41 (0)22 548 33 37
                        </p>
                    </>
                )}
                <div className="modal-buttons">
                    <button onClick={() => setDuoModalOpen(false)} className="btn btn-secondary">
                        {t('commande_edit:close')}
                    </button>
                </div>
            </Modal>

            {showTherapistDisclaimer && (
                <Modal
                    isOpen={showTherapistDisclaimer}
                    onRequestClose={() => setShowTherapistDisclaimer(false)}
                    style={{
                        content: {
                            width: "90%",
                            maxWidth: "420px",
                            maxHeight: "450px",
                            margin: "auto",
                            padding: "30px",
                            borderRadius: "12px",
                            textAlign: "center",
                        }
                    }}
                >
                    <h2>{t('commande_edit:change_therapist')}</h2>
                    <p>
                        {t('commande_edit:address_change_disclaimer')}
                        <br /><br />
                        {t('commande_edit:address_change_disclaimer2')}<br />
                        {t('commande_edit:address_change_disclaimer3')}
                    </p>
                    <div className="modal-buttons">
                        <button onClick={() => setShowTherapistDisclaimer(false)} className="btn btn-secondary">
                            {t('commande_edit:cancel')}
                        </button>
                        <button onClick={confirmTherapistChange} className="btn btn-primary">
                            {t('commande_edit:continue')}
                        </button>
                    </div>
                </Modal>
            )}

            {showSoinDisclaimer && (
                <Modal
                    isOpen={showSoinDisclaimer}
                    onRequestClose={() => setShowSoinDisclaimer(false)}
                    style={{
                        content: {
                            width: "90%",
                            maxWidth: "420px",
                            maxHeight: "420px",
                            margin: "auto",
                            padding: "30px",
                            borderRadius: "12px",
                            textAlign: "center",
                        }
                    }}
                >
                    <h2>{t('commande_edit:change_treatment_title')}</h2>

                    <p>
                        {t('commande_edit:change_treatment_disclaimer')}<br />
                        {t('commande_edit:change_treatment_disclaimer2')}<br />
                        {t('commande_edit:change_treatment_disclaimer3')}
                    </p>
                    <div className="modal-buttons">
                        <button onClick={() => setShowSoinDisclaimer(false)} className="btn btn-secondary">
                            {t('commande_edit:cancel')}
                        </button>
                        <button onClick={confirmSoinChange} className="btn btn-primary">
                            {t('commande_edit:continue')}
                        </button>
                    </div>
                </Modal>
            )}

            {showZoneOverrideConfirm && (
                <Modal
                    isOpen
                    onRequestClose={handleZoneOverrideCancel}
                    className="admin-zone-override-modal"
                    overlayClassName="admin-zone-override-overlay"
                >
                    <h2>{t('commande_edit:zone_override.title')}</h2>
                    <p>
                        {t('commande_edit:zone_override.message', {
                            flypros: zoneOverrideFlypros
                                .map(getFlyproLabel)
                                .join(', '),
                        })}
                    </p>
                    <div className="admin-zone-override-actions">
                        <button
                            type="button"
                            onClick={handleZoneOverrideCancel}
                            className="btn btn-secondary"
                            disabled={isProcessing}
                        >
                            {t('commande_edit:zone_override.cancel')}
                        </button>
                        <button
                            type="button"
                            onClick={handleZoneOverrideConfirm}
                            className="btn btn-primary"
                            disabled={isProcessing}
                        >
                            {t('commande_edit:zone_override.confirm')}
                        </button>
                    </div>
                </Modal>
            )}

            {showBookingConfirmationModal && (
                <Modal
                    isOpen={showBookingConfirmationModal}
                    onRequestClose={() => setShowBookingConfirmationModal(false)}
                    style={{
                        content: {
                            width: "90%",
                            maxWidth: "420px",
                            maxHeight: "350px",
                            margin: "auto",
                            padding: "30px",
                            borderRadius: "12px",
                            textAlign: "center",
                        }
                    }}
                >
                    <h2>{t('commande_edit:change_date_title')}</h2>
                    <p>
                    {currentTherapistIsAvailable ? (
                            <>
                                ✅ <span
                                        dangerouslySetInnerHTML={{
                                        __html: t('commande_edit:flypro_change_disclaimer', {
                                            selectedDate: `<strong>${formattedDate}</strong>`,
                                            selectedTime: `<strong>${formattedTime}</strong>`,
                                        })
                                    }}
                                /><br />
                                {t('commande_edit:flypro_change_success')}
                            </>
                            ) : (
                            <>
                                {t('commande_edit:therapist_not_available_specific', { date: formattedDate, time: formattedTime })}<br />
                                {t('commande_edit:select_other_slot')}
                            </>
                        )}
                    </p>
                    <div className="modal-buttons" style={{ marginTop: 20 }}>
                        <button onClick={() => setShowBookingConfirmationModal(false)} className="btn btn-secondary">
                        {t('commande_edit:cancel')}
                        </button>
                        {currentTherapistIsAvailable && (
                            <button
                                onClick={async () => {
                                    setIsProcessing(true);
                                    loader.start();
                                    try {
                                        await BookingService.update(booking?.id, pendingBookingPayload, axiosPrivate);
                                        onUpdate &&
                                            onUpdate({
                                                ...booking,
                                                products: selectedProducts.filter(Boolean),
                                                product: !duo ? selectedProducts[0] : booking?.product,
                                                price: pendingBookingPayload.price,
                                            });
                                        navigate("/tasks");
                                    } catch (err) {
                                        console.error("Erreur lors de la mise à jour:", err);
                                        setErrorMessage(t('commande_edit:update_error'));
                                    } finally {
                                        setIsProcessing(false);
                                        loader.stop();
                                        setShowBookingConfirmationModal(false);
                                    }
                                }}
                                className="btn btn-primary"
                            >
                            {t('commande_edit:continue')}
                            </button>
                        )}
                    </div>
                </Modal>
            )}

            {productChangeModalOpen && (
                <Modal
                    isOpen={productChangeModalOpen}
                    onRequestClose={() => setProductChangeModalOpen(false)}
                    style={{
                        content: {
                            width: "90%",
                            maxWidth: "480px",
                            maxHeight: "500px",
                            margin: "auto",
                            padding: "50px",
                            borderRadius: "12px",
                            textAlign: "center"
                        }
                    }}
                >
                    {showRefundSuccess ? (
                        <>
                            <h2>{t('commande_edit:treatment_change_confirmed')}</h2>
                            <p>
                                {t('commande_edit:treatment_change_success', { amount: Math.round(priceDifference * 0.96) })}<br />
                                {t('commande_edit:treatment_change_email')}                            </p>
                            <button
                                className="btn btn-primary"
                                onClick={() => { setProductChangeModalOpen(false); navigate("/commande"); }}
                            >
                                {t('commande_edit:close')}
                            </button>
                        </>
                    ) : isProcessingChange ? (
                        <>
                            <h2>{t('commande_edit:please_wait')}</h2>
                            <p>{t('commande_edit:dont_close_page')}</p>
                        </>
                    ) : (
                        <>
                            <h2>{t('commande_edit:product_change_title')}</h2>
                            {productChangeDirection === "increase" ? (
                                <>
                                    <p>
                                        {t('commande_edit:increase_duration_message')}<br />
                                        {t('commande_edit:additional_cost', { amount: priceDifference })}
                                    </p>
                                    <div className="modal-buttons" style={{ marginTop: 24, display: 'flex', gap: 12, justifyContent: 'center' }}>
                                        <button
                                            className="btn btn-primary"
                                            onClick={async () => {
                                                setProductChangeModalOpen(false);
                                                setIsProcessingChange(true);
                                                loader.start();
                                                try {
                                                    const { _edit_quote, ...successUpdate } = pendingBookingPayload;
                                                    const payResponse = await BookingService.payDifference(
                                                        {
                                                            booking_id: booking?.id,
                                                            new_total: _edit_quote?.new_net_total,
                                                            duration: pendingBookingPayload.duration,
                                                            success_update: successUpdate,
                                                        },
                                                        axiosPrivate
                                                    );

                                                    const lightboxUrl = payResponse.transaction_data.data.payment_lightbox;
                                                    const paymentUrl = payResponse.transaction_data.data.payment_url;

                                                    if (lightboxUrl && paymentUrl) {
                                                        showWalleeLightbox(lightboxUrl, paymentUrl, async () => {
                                                            onUpdate?.({
                                                                ...booking,
                                                                products: selectedProducts.filter(Boolean),
                                                                price: _edit_quote?.new_net_total
                                                            });
                                                            navigate("/commande");
                                                            loader.stop();
                                                        });

                                                        sessionStorage.setItem(
                                                            "pendingBookingUpdate",
                                                            JSON.stringify({
                                                                bookingId: booking?.id,
                                                                updatedPayload: successUpdate,
                                                                hydratedProducts: selectedProducts
                                                            })
                                                        );
                                                    } else {
                                                        loader.stop();
                                                    }
                                                } catch (err) {
                                                    console.error("Erreur lors du paiement de la différence:", err);
                                                    notify.error(t('commande_edit:payment_error'));
                                                    loader.stop();

                                                }
                                                finally {
                                                    setIsProcessingChange(false);
                                                }
                                            }}
                                        >
                                            {t('commande_edit:pay_difference')}
                                        </button>
                                        <button
                                            className="btn btn-secondary"
                                            onClick={() => setProductChangeModalOpen(false)}
                                        >
                                            {t('commande_edit:cancel')}
                                        </button>
                                    </div>
                                </>
                            ) : (
                                <>
                                    <p>
                                        {t('commande_edit:decrease_duration_message')}<br />
                                        {t('commande_edit:refund_choice')}
                                    </p>
                                    <div className="modal-buttons" style={{ marginTop: 24, display: 'flex', gap: 12, justifyContent: 'center' }}>
                                        <button
                                            className="btn btn-primary"
                                            onClick={handleProductRefund}
                                        >
                                            {t('commande_edit:refund')}
                                        </button>
                                        <button
                                            className="btn btn-primary"
                                        // onClick={handleIssueCredit}
                                        >
                                            {t('commande_edit:issue_credit')}
                                        </button>
                                    </div>
                                    <div>
                                        <button
                                            className="btn btn-secondary"
                                            onClick={() => setProductChangeModalOpen(false)}
                                        >
                                            {t('commande_edit:cancel')}
                                        </button>
                                    </div>
                                </>
                            )}
                        </>
                    )}

                </Modal >
            )
            }

            <DuoInfoModal
                open={duoInfoOpen}
                onClose={() => setDuoInfoOpen(false)}
                t1={therapist1Info}
                t2={therapist2Info}
                onAskRemove={(i) => { setRemoveIdx(i); setConfirmDeleteOpen(true); }}
            />

            <Modal
                isOpen={confirmDeleteOpen}
                onRequestClose={() => setConfirmDeleteOpen(false)}
                style={{
                    content: {
                        width: '90%', maxWidth: 420, margin: 'auto',
                        padding: 30, borderRadius: 12, textAlign: 'center'
                    }
                }}
            >
                <h2>{t('commande_edit:delete_therapist_title')}</h2>
                <p>
                    {t('commande_edit:delete_therapist_disclaimer')}
                    {t('commande_edit:delete_therapist_warning')}
                </p>

                <div style={{ display: 'flex', gap: 14, justifyContent: 'center', marginTop: 30 }}>
                    <button
                        className="btn btn-secondary"
                        onClick={() => setConfirmDeleteOpen(false)}
                    >
                        {t('commande_edit:cancel')}
                    </button>

                    <button
                        className="btn btn-danger"
                        onClick={() => deleteTherapistAndProducts(removeIdx)}
                    >
                        {t('commande_edit:delete')}
                    </button>
                </div>
            </Modal>

            <Modal
                isOpen={showProductConfirmModal}
                onRequestClose={() => setShowProductConfirmModal(false)}
                style={{
                    content: {
                        width: "90%",
                        maxWidth: "420px",
                        maxHeight: "420px",
                        margin: "auto",
                        padding: "30px",
                        borderRadius: "12px",
                        textAlign: "center",
                    }
                }}
            >
                <h2>{t('commande_edit:treatment_change_confirm_title')}</h2>
                <p>
                    {t('commande_edit:treatment_change_confirm_message')}
                </p>
                <div className="modal-buttons">
                    <button
                        className="btn btn-secondary"
                        onClick={() => setShowProductConfirmModal(false)}
                    >
                        {t('commande_edit:cancel')}
                    </button>
                    <button
                        className="btn btn-primary"
                        onClick={async () => {
                            setShowProductConfirmModal(false);
                            setIsProcessing(true);
                            try {
                                loader.start();
                                const { _edit_quote, ...updatePayload } = pendingUpdatePayload;
                                await BookingService.update(booking?.id, updatePayload, axiosPrivate);
                                onUpdate &&
                                    onUpdate({
                                        ...booking,
                                        products: selectedProducts.filter(Boolean),
                                        product: !duo ? selectedProducts[0] : booking?.product,
                                        price: _edit_quote?.new_net_total,
                                    });
                                navigate("/tasks");
                            } catch (error) {
                                setErrorMessage(t('commande_edit:update_error'));
                            } finally {
                                setIsProcessing(false);
                                loader.stop();
                            }
                        }}
                    >
                        {t('commande_edit:confirm')}
                    </button>
                </div>
            </Modal>

        </>
    );
}

export default BookingEditAdmin;
